import React from "react";
import { validateChildComponent, validChildWrapperComponent } from "../../../../shared/validation";
import { ComponentValidator, validateComponent } from "../../../../shared/validation";
import { Persona } from "../../../Persona/Persona";
import { StackProps } from "../../../Stacks/Stack/Stack";
import { Text } from "../../../Text/Text";
import { InternalListItemPersonaReflow } from "../blocks/InternalListItemPersonaReflow/InternalListItemPersonaReflow";
import { ListItemMedia } from "../blocks/ListItemMedia/ListItemMedia";
import { FinalColumnAlign, validColumnComponents, validFinalColumnActionComponents } from "./MultiColumnListItem.types";

const listItemParentValidationName = "ListItem";
const maxFinalColumns = 2;

export const validateListItemColumnComponent = (component: React.ReactNode, validColumnItems: readonly React.ComponentType<any>[]) => {
    return validateChildComponent(component, validColumnItems, listItemParentValidationName);
};

const listItemColumnComponentValidator = (isClickable: boolean, actionCount: number): ComponentValidator => (
    () => (isClickable && actionCount > maxFinalColumns ? `Clickable list item cannot have more than ${maxFinalColumns} final column actions but has ${actionCount}.` : undefined)
);

export const validateNumberOfFinalColumnActions = (listItemPrefix: string, isClickable: boolean, actionCount: number) => {
    validateComponent(`${listItemPrefix}ListItemInternal`, [listItemColumnComponentValidator(isClickable, actionCount)]);
};

// A HOC that can be used to wrap valid ListItemFinalColumn action components, such that if a stateful-wrapper renders a valid ListItemColumn component,
// the validation will look at the output of the stateful component, rather than failing on the different name
export function listItemFinalColumnActionComponent<T extends {}>(component: React.FC<T>): React.FC<T> {
    return validChildWrapperComponent(component, validFinalColumnActionComponents, listItemParentValidationName);
}

// A HOC that can be used to wrap valid ListItemColumn components, such that if a stateful-wrapper renders a valid ListItemColumn component,
// the validation will look at the output of the stateful component, rather than failing on the different name
export function listItemColumnComponent<T extends {}>(component: React.FC<T>): React.FC<T> {
    return validChildWrapperComponent(component, validColumnComponents, listItemParentValidationName);
}

// Alignment for the final column action buttons.
// Start means the action buttons will align to the left, on the right side of any final column text.
// End means the action button will align to the right, far from the final column text.
export const multiColumnConsumerToStackAlignment: { readonly [id in FinalColumnAlign]: StackProps["horizontalAlign"] } = {
    start: "start",
    end: "end"
};

/**
 * Takes the first column of a OneColumnListItem and merges all the non-media
 * components with the media component (Media / Persona)
 *
 * @param firstColumn Items to be put into the first column of a OneColumnListItem
 */
export const listItemMediaMerge = (firstColumn: readonly JSX.Element[]) => {

    let firstColumnGraphic: JSX.Element | undefined;

    const listItemMedia: JSX.Element | undefined = firstColumn.find((firstColumnComponent) => (
        firstColumnComponent.type === ListItemMedia || firstColumnComponent.type === Persona
    ));

    const firstColumnNonMediaOrPersona: JSX.Element[] = firstColumn.filter((item) => item !== listItemMedia);

    // If there's a Media or Persona component in the first column we:
    //    if it's a Media, we add the other components to its children
    //    if it's a Persona, we remove the text from it, and then add
    //        that text and the other components as children to a specific
    //        InternalListItemPersonaReflow component
    if (listItemMedia) {
        if (listItemMedia.type === Persona) {
            const combinedFirstColumnComponents = withKeys([<Text>{listItemMedia.props.text}</Text>, ...firstColumnNonMediaOrPersona]);

            firstColumnGraphic = (
                <InternalListItemPersonaReflow
                    personaGraphic={React.cloneElement(listItemMedia, { ...listItemMedia.props, text: undefined })}
                >
                    {combinedFirstColumnComponents}
                </InternalListItemPersonaReflow>
            );
        } else {
            const combinedFirstColumnComponents = withKeys([listItemMedia.props.children, ...firstColumnNonMediaOrPersona]);

            firstColumnGraphic = React.cloneElement(listItemMedia, undefined, combinedFirstColumnComponents);
        }
    }

    if (firstColumnGraphic) {
        return [firstColumnGraphic];
    } else {
        return firstColumn;
    }
};

const withKeys = (elements: readonly (JSX.Element | undefined)[]) => (
    elements.map((item, index) => (!item || item.key !== null) ? item : React.cloneElement(item, { key: index }))
);
